import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import imageCompression from "browser-image-compression";
import { useI18n } from "../i18n";
import { ApiError, getConfig, getWall, postWall, type WallPost } from "../lib/api";
import { Turnstile } from "../components/Turnstile";
import { ArrowLeft, Heart, Send } from "../components/Icon";

export function Wall() {
  const { t } = useI18n();
  const [sitekey, setSitekey] = useState("");
  const [posts, setPosts] = useState<WallPost[]>([]);

  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getConfig().then((c) => setSitekey(c.turnstileSitekey)).catch(() => {});
    getWall().then(setPosts).catch(() => {});
  }, []);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return;
    setError("");
    try {
      const compressed = await imageCompression(f, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
      });
      const out = new File([compressed], f.name.replace(/\.[^.]+$/, "") + ".jpg", {
        type: compressed.type || "image/jpeg",
      });
      setFile(out);
      setPreview(URL.createObjectURL(out));
    } catch {
      // Fall back to the original if compression fails.
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  }

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError("");
    if (!message.trim() && !file) {
      setError(t("wall.emptyErr"));
      return;
    }
    if (sitekey && !token) {
      setError(t("reserve.errors.turnstile"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (name.trim()) fd.append("display_name", name.trim());
      if (message.trim()) fd.append("message", message.trim());
      if (file) fd.append("image", file);
      if (token) fd.append("turnstileToken", token);
      await postWall(fd);
      setDone(true);
      setName("");
      setMessage("");
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      if (err instanceof ApiError && err.code === "rate_limited") setError(t("wall.rateLimited"));
      else if (err instanceof ApiError && err.code === "image_too_large") setError(t("wall.tooLarge"));
      else setError(t("reserve.errors.generic"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container-x max-w-4xl py-10">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-[var(--theme-deep)]">
        <ArrowLeft className="h-4 w-4" /> {t("common.backHome")}
      </Link>
      <h1 className="section-title mt-3 flex items-center gap-2">
        {t("wall.title")} <Heart className="h-6 w-6" />
      </h1>
      <p className="mt-1 text-ink/70">{t("wall.subtitle")}</p>

      {/* Upload form */}
      <form onSubmit={onSubmit} className="card mt-6">
        {done ? (
          <div className="py-6 text-center">
            <span className="icon-badge mx-auto h-14 w-14"><Heart className="h-7 w-7" /></span>
            <p className="mt-3 font-semibold text-[var(--theme-deep)]">{t("wall.pendingMsg")}</p>
            <button type="button" onClick={() => setDone(false)} className="btn-ghost mt-4">
              {t("wall.post")}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="field-label">{t("wall.nameLabel")}</label>
                <input className="field" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
              </div>
              <div>
                <label className="field-label">{t("wall.photoLabel")}</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickFile}
                  className="field file:mr-3 file:rounded-full file:border-0 file:bg-[var(--theme-soft)] file:px-4 file:py-1.5 file:font-semibold file:text-[var(--theme-deep)]"
                />
              </div>
            </div>
            <div>
              <label className="field-label">{t("wall.messageLabel")}</label>
              <textarea
                className="field min-h-[90px] resize-y"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={300}
              />
            </div>
            {preview && (
              <img src={preview} alt="preview" className="max-h-56 rounded-2xl object-contain ring-1 ring-black/5" />
            )}
            {sitekey && <Turnstile sitekey={sitekey} onToken={setToken} />}
            {error && <p className="text-center text-sm font-semibold text-red-500">{error}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full">
              <Send className="h-4 w-4" /> {busy ? t("wall.posting") : t("wall.post")}
            </button>
          </div>
        )}
      </form>

      {/* Gallery — a short preview; the full browsable feed lives on /wishes. */}
      <div className="mt-10 flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-[var(--theme-deep)]">{t("wall.galleryTitle")}</h2>
        <Link to="/wishes" className="btn-ghost px-4 py-2 text-sm">
          {t("wall.seeAll")}
        </Link>
      </div>
      {posts.length === 0 ? (
        <p className="mt-4 text-center text-ink/50">{t("wall.emptyGallery")}</p>
      ) : (
        <div className="mt-4 columns-2 gap-4 md:columns-3 [&>*]:mb-4">
          {posts.slice(0, 12).map((p) => (
            <WallCard key={p.id} post={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function WallCard({ post }: { post: WallPost }) {
  return (
    <div className="break-inside-avoid rounded-2xl bg-white/80 p-3 shadow-card ring-1 ring-black/5">
      {post.has_image && (
        <img
          src={`/api/wall/img/${post.id}`}
          alt={post.message ?? "wish"}
          loading="lazy"
          className="mb-2 w-full rounded-xl object-cover"
        />
      )}
      {post.message && <p className="whitespace-pre-wrap text-sm text-ink/80">{post.message}</p>}
      {post.display_name && <p className="mt-1 text-xs font-semibold text-[var(--theme-deep)]">— {post.display_name}</p>}
    </div>
  );
}
